import { FC } from 'react'

import { Box, Table, Text } from '@factorialco/deprecated-design-system'
import { useUnsafeMemo } from '@factorialco/deprecated-design-system'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import i18n from 'modules/core/lib/i18n'

import { useFinancialWorkspace } from 'modules/finance/components/FinancialWorkspace/Providers/Provider'
import { useTotal } from 'modules/finance/components/FinancialWorkspace/Providers/TotalProvider'
import {
  DEFAULT_CURRENCY,
  stickyColumnProps,
} from 'modules/finance/components/FinancialWorkspace/components/Table/const'
import { PlotData } from 'modules/finance/components/FinancialWorkspace/components/Table/types'
import {
  InsightTableType,
  PaginatedTableItem,
} from 'modules/finance/components/FinancialWorkspace/types/table'
import { Amount } from 'modules/project_management/components/Amount'

import { Gap } from './Gap'

interface Props {
  items: PaginatedTableItem[]
}

export const Ungrouped: FC<Props> = ({ items }) => {
  const { company } = useCurrentSession()
  const { supportedCurrencies, formattedState } = useFinancialWorkspace()
  const currency = supportedCurrencies.includes(String(company.currency))
    ? company.currency
    : DEFAULT_CURRENCY

  const graphData = useTotal()

  const ungroupedData: number[] = useUnsafeMemo(() => {
    if (!graphData.loading && !graphData.error) {
      const plotObject: Record<string, PlotData> = {}
      const graphPlotData = graphData.plotData || []

      graphPlotData.forEach(({ data: conceptData }) => {
        conceptData.forEach(({ x, title, y, totalAmountWithPrecision }) => {
          if (!plotObject[x]) {
            plotObject[x] = { x, title, y: 0 }
          }
          plotObject[x].y =
            Number(plotObject[x].y || 0) + Number(totalAmountWithPrecision || y || 0)
        })
      })

      const differences: number[] = []
      const plotData = Object.values(plotObject)

      plotData.forEach(({ y, totalAmountWithPrecision }, idx) => {
        const graphValue = Number(totalAmountWithPrecision || y || 0)
        const tableSum = items.reduce((acc, cur) => {
          const item = cur.data?.[idx]
          const itemValue = Number(item?.totalAmountWithPrecision || item?.totalAmountInCents || 0)
          return acc + itemValue
        }, 0)
        differences.push(Math.round(graphValue - tableSum))
      })

      return differences
    }

    return []
  }, [graphData, items])

  const showUngroupedData = useUnsafeMemo(
    () => ungroupedData.some((difference) => !!difference),
    [ungroupedData]
  )

  if (formattedState.insightTableType === InsightTableType.Payroll) {
    return null
  }

  const tableIsNested = items.some((item) => !!item.children?.length || !!item.categoryLabel)

  return showUngroupedData ? (
    <>
      {tableIsNested && <Gap colSpan={ungroupedData.length + 1} />}
      <Table.Row>
        <Table.Cell wide sticky={stickyColumnProps}>
          <Box style={{ paddingLeft: tableIsNested ? '28px' : undefined }}>
            <Text.Normal
              ellipsis
              size="200"
              color={tableIsNested ? 'grey1000' : 'grey800'}
              weight={tableIsNested ? 'semibold' : 'medium'}
            >
              {i18n.t('finance.financial_workspace.unassigned')}
            </Text.Normal>
          </Box>
        </Table.Cell>
        {ungroupedData.map((amount, index) => (
          <Table.Cell wide key={`ungrouped-${index}`}>
            <Box gap="s8" flexDirection="row" alignItems="center" justifyContent="flexEnd">
              {amount && tableIsNested ? (
                <Text.Normal size="200">
                  {i18n.t('finance.financial_workspace.payroll.table.sum')}
                </Text.Normal>
              ) : null}
              <Amount
                size="200"
                optionalDecimals={false}
                amount={Math.abs(amount)}
                currency={String(currency)}
                color={tableIsNested ? 'grey1000' : 'grey800'}
                weight={tableIsNested ? 'semibold' : 'normal'}
              />
            </Box>
          </Table.Cell>
        ))}
      </Table.Row>
    </>
  ) : null
}
